import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { CircleDollarSign, Leaf, Zap, Cpu, ExternalLink, Coins } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export default function GrantsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();

  // Parallax scroll
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y1 = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [-60, 60]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const areas = [
    { icon: Zap, label: t('grants.area1'), color: '#f97316' },
    { icon: Leaf, label: t('grants.area2'), color: '#22c55e' },
    { icon: CircleDollarSign, label: t('grants.area3'), color: '#3b82f6' },
    { icon: Cpu, label: t('grants.area4'), color: '#8DC21F' },
  ];

  return (
    <section ref={ref} className="py-20 sm:py-28 relative overflow-hidden">
      {/* Animated money/coins background */}
      <motion.div 
        style={{ y: y1, opacity }}
        className="absolute inset-0 pointer-events-none"
      >
        <div className="absolute top-1/4 left-1/3 w-80 h-80 rounded-full blur-3xl opacity-10" style={{ background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)' }} />
        <motion.div 
          style={{ y: y2, rotate }}
          className="absolute bottom-1/4 right-1/4 opacity-10"
        >
          <Coins size={100} className="text-[#8DC21F]" />
        </motion.div>
      </motion.div>

      <div className="v-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <motion.span 
            className="v-tag green mb-6 inline-flex"
            whileHover={{ 
              scale: 1.15,
              boxShadow: '0 0 30px rgba(141,194,31,0.4)'
            }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            {t('grants.tag')}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-4 tracking-tight">
            {t('grants.h1a')}{' '}
            <motion.span 
              style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', backgroundSize: '200% auto' }}
              animate={{ 
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
              }}
              transition={{ duration: 5, repeat: Infinity }}
              className="bg-gradient-to-r from-[#8DC21F] via-[#5A8C1A] to-[#8DC21F] bg-clip-text text-transparent"
            >
              {t('grants.h1b')}
            </motion.span>
          </h2>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 max-w-3xl mx-auto leading-relaxed">
            {t('grants.sub')}
          </p>
        </motion.div>

        {/* Areas with magnetic hover effect */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {areas.map((area, i) => {
            const Icon = area.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 50, rotateY: -45 }}
                animate={inView ? { opacity: 1, y: 0, rotateY: 0 } : {}}
                transition={{ 
                  duration: 0.9, 
                  delay: i * 0.12,
                  type: "spring",
                  stiffness: 80
                }}
                whileHover={{ 
                  y: -15, 
                  scale: 1.08,
                  rotateY: 8,
                  rotateX: -5,
                  transition: { duration: 0.3 }
                }}
                className="liquid-glass rounded-2xl p-6 flex flex-col items-center text-center group cursor-pointer relative overflow-hidden"
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Animated glow pulse */}
                <motion.div
                  className="absolute inset-0"
                  animate={{ 
                    boxShadow: [
                      `inset 0 0 0px ${area.color}00`,
                      `inset 0 0 40px ${area.color}30`,
                      `inset 0 0 0px ${area.color}00`,
                    ]
                  }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.5 }}
                />

                <motion.div
                  className="w-14 h-14 rounded-xl mb-4 flex items-center justify-center relative z-10"
                  style={{ background: `${area.color}15`, border: `1px solid ${area.color}30` }}
                  whileHover={{ 
                    scale: 1.4,
                    rotate: [0, -15, 15, -10, 10, 0],
                    boxShadow: `0 0 30px ${area.color}40`
                  }}
                  transition={{ duration: 0.7 }}
                >
                  <Icon size={24} style={{ color: area.color }} />
                </motion.div>
                
                <p className="text-sm font-semibold text-black dark:text-white/80 relative z-10">
                  {area.label}
                </p>

                {/* Orbiting particles */}
                <motion.div
                  className="absolute top-2 right-2 w-2 h-2 rounded-full opacity-0 group-hover:opacity-100"
                  style={{ background: area.color }}
                  animate={{
                    scale: [0, 1, 1, 0],
                    opacity: [0, 1, 1, 0]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />

                {/* Shine sweep */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '100%' }}
                  transition={{ duration: 0.6 }}
                />
              </motion.div>
            );
          })}
        </div>

        {/* CTA with magnetic button effect */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.6 }}
          whileHover={{ scale: 1.02 }}
          className="liquid-glass rounded-3xl p-8 text-center relative overflow-hidden"
        >
          {/* Radial gradient background */}
          <motion.div 
            className="absolute inset-0"
            animate={{ 
              background: [
                'radial-gradient(circle at 50% 50%, rgba(141,194,31,0.05) 0%, transparent 70%)',
                'radial-gradient(circle at 50% 50%, rgba(141,194,31,0.12) 0%, transparent 70%)',
                'radial-gradient(circle at 50% 50%, rgba(141,194,31,0.05) 0%, transparent 70%)',
              ]
            }}
            transition={{ duration: 4, repeat: Infinity }}
          />

          <p className="text-sm text-gray-700 dark:text-white/70 mb-5 relative z-10">
            {t('grants.ctaText')}
          </p>
          
          <motion.a
            href="mailto:innovation@valorizasm.com"
            whileHover={{ 
              scale: 1.08,
              boxShadow: '0 12px 40px rgba(141,194,31,0.35)'
            }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-black transition-all relative z-10"
            style={{ background: '#8DC21F', boxShadow: '0 6px 20px rgba(141,194,31,0.25)' }}
          >
            <motion.span
              animate={{ x: [0, 3, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              {t('grants.ctaButton')}
            </motion.span>
            <motion.div
              animate={{ x: [0, 4, 0], opacity: [1, 0.7, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ExternalLink size={16} />
            </motion.div>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}
